import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { handleUpdateUser } from "../../api/user.api";
import Button, { ButtonType } from "../../components/Assets/Button";
import LocationSelector from "../../components/Users/LocationSelector";
import { IUser } from "../../models/userData.model";
import ToggleSwitch from "../../components/Users/ToggleSwitch";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import NavigationAccessModal from "../../components/NavigationAccessModal";
import { getRoleNavigationByRole } from "../../store/slices/userSlice";

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userData: IUser;
  statesData: { state: string; districts: string[] }[];
  roles: string[];
  users: IUser[];
  onUserUpdated: () => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  userId,
  userData,
  statesData,
  roles,
  users,
  onUserUpdated,
}) => {
  const { warehouseNamesAndIds: warehouses } = useSelector(
    (state: RootState) => state.warehouse,
  );
  const dispatch: RootDispatch = useDispatch();
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);

  const [formData, setFormData] = useState<IUser>({
    username: userData?.username || "",
    email: userData?.email || "",
    mobileNumber: userData?.mobileNumber || "",
    role: userData?.role || "",
    manager:
      typeof userData?.manager === "object"
        ? (userData?.manager as IUser)?._id
        : userData?.manager,
    states: userData?.states || [],
    districts: userData?.districts || [],
    warehouseAssigned: userData?.warehouseAssigned || "",
    newPassword: "",
    isGeoLocationEnabled: userData?.isGeoLocationEnabled,
    trackLocation: userData?.trackLocation ?? false,
    navigations: userData?.navigations || {
      main: [],
      extras: [],
      frelit: [],
      notifications: null,
      customerNotifications: null,
    },
  });

  useEffect(() => {
    const isNavigationEmpty = (nav: any) =>
      !nav ||
      (!nav.main?.length &&
        !nav.extras?.length &&
        !nav.frelit?.length &&
        !nav.notifications &&
        !nav.customerNotifications);

    const initNavigation = async () => {
      if (!isOpen || !userData) return;

      if (!isNavigationEmpty(userData.navigations)) {
        setFormData((prev) => ({
          ...prev,
          navigations: userData.navigations,
        }));
        return;
      }

      try {
        const res = await dispatch(
          getRoleNavigationByRole(userData.role),
        ).unwrap();

        setFormData((prev) => ({
          ...prev,
          navigations: res,
        }));
      } catch (err) {
        console.error("Failed to fetch role navigation", err);
      }
    };

    initNavigation();
  }, [isOpen, userData, dispatch]);

  useEffect(() => {
    if (isOpen && userData) {
      setFormData({
        username: userData.username,
        email: userData.email,
        mobileNumber: userData.mobileNumber,
        role: userData.role,
        manager:
          typeof userData?.manager === "object"
            ? (userData?.manager as IUser)?._id
            : userData?.manager,
        states: userData.states ?? [],
        districts: userData.districts ?? [],
        warehouseAssigned: userData.warehouseAssigned ?? "",
        newPassword: "",
        isGeoLocationEnabled: userData.isGeoLocationEnabled,
        trackLocation: userData.trackLocation,
        navigations: userData.navigations || {
          main: [],
          extras: [],
          frelit: [],
          notifications: null,
          customerNotifications: null,
        },
      });
    }
  }, [isOpen, userData]);

  const hideLocations = [
    "admin",
    "factory",
    "account",
    "data entry",
    "sp",
    "hr",
  ];

  // const getManagerOptions = () => {
  //   if (!users) return [];
  //   if (formData.role === "bde" || formData.role === "telecaller") {
  //     return users.filter((u: IUser) => u && u.role === "bdm");
  //   } else if (formData.role === "bdm") {
  //     return users.filter((u: IUser) => u && u.role === "sh");
  //   } else if (formData.role === "sh") {
  //     return users.filter((u: IUser) => u && u.role === "admin");
  //   }
  //   return users.filter((u: IUser) => u);
  // };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    if (name === "role") {
      setFormData((prev) => ({
        ...prev,
        role: value,
        manager: "",
        states: [],
        districts: [],
        warehouseAssigned: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    if (
      !formData.username ||
      !formData.email ||
      !formData.mobileNumber ||
      !formData.role
    ) {
      toast.error("Please fill in all required fields");
      return false;
    }

    if (formData.mobileNumber.length !== 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return false;
    }

    if (["factory", "floor", "store"].includes(formData.role)) {
      if (!formData.warehouseAssigned) {
        toast.error("Please select a warehouse");
        return false;
      }
    }

    if (formData.role !== "admin") {
      if (!formData.manager) {
        toast.error("Please select a manager for this role");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      const updatedFields: Partial<IUser> = {};

      Object.keys(formData).forEach((key) => {
        if (key === "newPassword") {
          if (formData.newPassword) {
            updatedFields.newPassword = formData.newPassword;
          }
        } else if (
          userData &&
          formData[key as keyof IUser] !== userData[key as keyof IUser]
        ) {
          updatedFields[key as keyof IUser] = formData[
            key as keyof IUser
          ] as never;
        }
      });

      if (Object.keys(updatedFields).length > 0) {
        await handleUpdateUser(userId, updatedFields);
        onUserUpdated();
        onClose();
      } else {
        toast.error("No changes detected");
      }
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Edit User</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 text-xs gap-4 mb-4">
          <div>
            <label className="block mb-1 text-xs font-medium">Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium">Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium">
              Mobile Number
            </label>
            <input
              type="text"
              name="mobileNumber"
              value={formData.mobileNumber}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium">Role</label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            >
              {roles.map((role) => (
                <option key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-1 text-xs font-medium">Warehouse</label>
            <select
              name="warehouseAssigned"
              value={formData.warehouseAssigned || ""}
              onChange={handleChange}
              className="w-full p-2 border rounded"
            >
              <option value="" disabled>
                Select Factory
              </option>
              {warehouses.map((warehouse: any) => (
                <option key={warehouse._id} value={warehouse._id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </div>

          {formData.role && formData.role !== "admin" && (
            <div>
              <label className="block mb-1 text-xs font-medium">Manager</label>
              <select
                name="manager"
                value={formData.manager || ""}
                onChange={handleChange}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Manager</option>
                {users.map((user: IUser) => (
                  <option key={user._id} value={user._id}>
                    {user.username} ({user.role})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block mb-1 text-xs font-medium">
              New Password
            </label>
            <input
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleChange}
              placeholder="Leave blank to keep current"
              className="w-full p-2 border rounded"
            />
          </div>
          <ToggleSwitch
            id="geoLocationToggle"
            checked={!!formData.isGeoLocationEnabled}
            onChange={(checked) =>
              setFormData((prev) => ({
                ...prev,
                isGeoLocationEnabled: checked,
              }))
            }
            sizeVariant="sm"
          />
        </div>
        <ToggleSwitch
          id="trackLocationToggle"
          label="Enable GPS Tracking"
          checked={!!formData.trackLocation}
          onChange={(checked) =>
            setFormData((prev) => ({
              ...prev,
              trackLocation: checked,
            }))
          }
          sizeVariant="sm"
        />

        <div className="py-4">
          <Button
            type={ButtonType.Cancel}
            onClick={() => setIsAccessModalOpen(true)}
            className="text-xs py-6! w-full"
          >
            Select Access
          </Button>
        </div>

        {!hideLocations.includes(formData.role) &&
          !["store", "floor"].includes(formData.role) && (
            <LocationSelector
              role={formData.role}
              states={formData.states ?? []}
              districts={formData.districts ?? []}
              onStatesChange={(states) =>
                setFormData((prev) => ({ ...prev, states }))
              }
              onDistrictsChange={(districts) =>
                setFormData((prev) => ({ ...prev, districts }))
              }
              statesData={statesData}
              required={[
                "sh",
                "bdm",
                "bde",
                "telecaller",
                "distributor",
              ].includes(formData.role)}
            />
          )}

        <div className="flex justify-end gap-3 mt-6">
          <Button
            type={ButtonType.Cancel}
            onClick={onClose}
            className="text-sm"
          >
            Close
          </Button>
          <Button
            type={ButtonType.Extra}
            onClick={handleSubmit}
            className="text-sm"
          >
            Update
          </Button>
        </div>
      </div>
      <NavigationAccessModal
        isOpen={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        navigations={formData.navigations}
        setNavigations={(nav) =>
          setFormData((prev) => ({
            ...prev,
            navigations: nav,
          }))
        }
      />
    </div>
  );
};

export default EditUserModal;
